import { toast } from 'react-hot-toast';

export interface ApiError {
  statusCode?: number;
  message: string;
  code?: string;
  retryAfter?: number;
}

export interface RetryConfig {
  maxRetries?: number;
  initialDelay?: number;
  maxDelay?: number;
  backoffMultiplier?: number;
}

const DEFAULT_RETRY_CONFIG: Required<RetryConfig> = {
  maxRetries: 3,
  initialDelay: 1000, // 1 second
  maxDelay: 10000, // 10 seconds
  backoffMultiplier: 2,
};

/**
 * Parse error from API response
 */
export function parseApiError(error: any): ApiError {
  // Handle GraphQL errors
  if (error.errors && Array.isArray(error.errors) && error.errors.length > 0) {
    const firstError = error.errors[0];
    return {
      statusCode: firstError.extensions?.statusCode,
      message: firstError.message || 'An error occurred',
      code: firstError.extensions?.code,
    };
  }

  // Handle HTTP errors
  if (error.response) {
    const statusCode = error.response.status;
    const data = error.response.data;

    return {
      statusCode,
      message: data?.message || data?.error || getDefaultErrorMessage(statusCode),
      code: data?.code,
      retryAfter: data?.retryAfter || error.response.headers?.['retry-after'],
    };
  }

  // Handle network errors
  if (error.request) {
    return {
      statusCode: 503,
      message: 'Unable to connect to the server. Please check your internet connection.',
      code: 'NETWORK_ERROR',
    };
  }

  // Handle other errors
  return {
    message: error.message || 'An unexpected error occurred',
    code: 'UNKNOWN_ERROR',
  };
}

/**
 * Get user-friendly error message based on status code
 */
function getDefaultErrorMessage(statusCode: number): string {
  switch (statusCode) {
    case 400:
      return 'Invalid request. Please check your input and try again.';
    case 401:
      return 'You need to be logged in to perform this action.';
    case 403:
      return 'You do not have permission to access this resource.';
    case 404:
      return 'The requested resource could not be found.';
    case 429:
      return 'Too many requests. Please slow down and try again in a moment.';
    case 500:
      return 'An internal server error occurred. Please try again later.';
    case 503:
      return 'The service is temporarily unavailable. Please try again later.';
    default:
      return 'An error occurred. Please try again.';
  }
}

/**
 * Display user-friendly error toast
 */
export function displayErrorToast(error: ApiError) {
  const message = error.message;
  
  if (error.statusCode === 429) {
    const retryMessage = error.retryAfter
      ? ` Please try again in ${error.retryAfter} seconds.`
      : ' Please try again in a moment.';
    toast.error(message + retryMessage, { duration: 5000 });
  } else if (error.statusCode === 503) {
    toast.error(message, { duration: 5000 });
  } else if (error.statusCode === 404) {
    toast.error(message, { duration: 4000 });
  } else {
    toast.error(message, { duration: 4000 });
  }
}

/**
 * Calculate delay for exponential backoff
 */
function calculateBackoffDelay(
  attempt: number,
  config: Required<RetryConfig>
): number {
  const delay = config.initialDelay * Math.pow(config.backoffMultiplier, attempt);
  return Math.min(delay, config.maxDelay);
}

/**
 * Sleep for specified milliseconds
 */
function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Determine if error is retryable
 */
function isRetryableError(error: ApiError): boolean {
  // Don't retry client errors (4xx) except 429 (rate limit)
  if (error.statusCode && error.statusCode >= 400 && error.statusCode < 500) {
    return error.statusCode === 429;
  }

  // Retry server errors (5xx) and network errors
  if (error.statusCode && error.statusCode >= 500) {
    return true;
  }

  // Retry network errors
  if (error.code === 'NETWORK_ERROR') {
    return true;
  }

  return false;
}

/**
 * Execute API call with exponential backoff retry logic
 */
export async function withRetry<T>(
  apiCall: () => Promise<T>,
  config: RetryConfig = {}
): Promise<T> {
  const retryConfig = { ...DEFAULT_RETRY_CONFIG, ...config };
  let lastError: ApiError | null = null;

  for (let attempt = 0; attempt <= retryConfig.maxRetries; attempt++) {
    try {
      return await apiCall();
    } catch (error: any) {
      lastError = parseApiError(error);

      // Don't retry if error is not retryable
      if (!isRetryableError(lastError)) {
        throw lastError;
      }

      // Don't retry if we've exhausted all attempts
      if (attempt === retryConfig.maxRetries) {
        throw lastError;
      }

      // Handle rate limiting with retry-after header
      if (lastError.statusCode === 429 && lastError.retryAfter) {
        const retryAfterMs = lastError.retryAfter * 1000;
        console.log(`Rate limited. Retrying after ${lastError.retryAfter} seconds...`);
        await sleep(retryAfterMs);
        continue;
      }

      // Calculate backoff delay
      const delay = calculateBackoffDelay(attempt, retryConfig);
      console.log(`Attempt ${attempt + 1} failed. Retrying in ${delay}ms...`);
      await sleep(delay);
    }
  }

  // This should never be reached, but TypeScript needs it
  throw lastError || new Error('Unknown error occurred');
}

/**
 * Handle API error with user feedback
 */
export function handleApiError(error: any, customMessage?: string): ApiError {
  const apiError = parseApiError(error);
  
  if (customMessage) {
    apiError.message = customMessage;
  }
  
  displayErrorToast(apiError);
  
  return apiError;
}
